/**
 * The profile photograph (`MEM-006`), in Your account's profile card.
 *
 * ## It is `Web App.dc.html` §2.15's head row, and it used to be a card
 *
 * It shipped as a block of its own with an uppercase *Profile photo* heading, a 76px avatar and
 * an *Upload photo* button, because the screen it was on had nothing else to be beside. §2.15
 * draws it as the profile card's own first row — a 72px avatar, the member's name over their
 * role, and *Change photo* opposite — and that is what it is now. The `card` variant is gone
 * rather than kept for a caller that might appear: it had none after the merge, and a second
 * arrangement of an upload control is a second place the argument below has to hold.
 *
 * ## This component does not strip anything, deliberately
 *
 * It picks a file, asks the API for an upload address, `PUT`s the bytes at it and tells the API it
 * is done. **Every embedded field is removed on the server**, between the second step and the
 * third, and there is no client-side resize or re-encode here that could be mistaken for the
 * privacy control.
 *
 * `docs/plans/stage-m1-member-registration.md` states the requirement in one line — *"Photographs
 * carry GPS. Stripping happens server-side"* — and the reason is that a client cannot be trusted
 * to do it: a member on an old browser, a script posting the same request, or a future refactor
 * that drops a canvas step would each produce an unstripped photograph on a profile. The way this
 * client cannot skip the strip is that it never holds the canonical key: the address it uploads to
 * is a `quarantine/` one that nothing serves, and the only thing that turns those bytes into a
 * profile photo is the server reading them back, stripping them and writing a different object.
 *
 * A canvas resize *would* incidentally drop EXIF, and that is exactly why it is not here — an
 * incidental privacy control is one nobody maintains.
 */
import { Avatar, Button, useErrorToast } from '@h2hspark/ui';
import { Upload } from 'lucide-react';
import * as React from 'react';

import { useRemoveProfilePhoto, useUploadProfilePhoto } from './api';
import { MEMBER_ACCOUNT_COPY } from './constants';

/** What the API will accept. Anything else is refused server-side; saying so first is kinder. */
const ACCEPTED = 'image/jpeg,image/png';

export function ProfilePhotoForm({
  photoUrl,
  fullName,
  roleLine,
}: {
  readonly photoUrl: string | null;
  /** §2.15's name, beside the avatar. */
  readonly fullName: string;
  /** *Founder, H2HSpark · Providence, RI*. `null` when there is none. */
  readonly roleLine: string | null;
}): React.ReactElement {
  const upload = useUploadProfilePhoto();
  const remove = useRemoveProfilePhoto();
  const showError = useErrorToast();
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (upload.error !== null) showError(upload.error, 'That photo did not upload.');
  }, [upload.error, showError]);
  React.useEffect(() => {
    if (remove.error !== null) showError(remove.error, 'That photo did not come off.');
  }, [remove.error, showError]);

  // Outside the visible flow, driven by the button, so the control is a button rather than a
  // browser-styled file input the design system cannot reach.
  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPTED}
      className="sr-only"
      aria-label={MEMBER_ACCOUNT_COPY.photoUpload}
      onChange={(event) => {
        const file = event.target.files?.[0];
        // Reset first: picking the same file twice must fire `change` again, which it does not
        // if the input still holds it.
        event.target.value = '';
        if (file !== undefined) upload.mutate(file);
      }}
    />
  );

  // §2.15's *Change photo*: the prototype's secondary pill — 1.5px `--border-default` on
  // `--surface-card`, `--text-body`, `font-brand` 700 `--text-sm`, `padding:9px 16px`, a 15px
  // upload glyph and a 6px gap. It was `variant="outline" size="sm"`, which is a 1px border on the
  // page colour at 32px and `--text-xs` — a smaller, flatter control than the one drawn.
  const pick = (
    <Button
      type="button"
      variant="outline-strong"
      size="pill"
      className="gap-1.5 px-4"
      disabled={upload.isPending}
      onClick={() => inputRef.current?.click()}
    >
      <Upload aria-hidden className="h-[15px] w-[15px]" />
      {upload.isPending ? 'Uploading…' : MEMBER_ACCOUNT_COPY.photoChange}
    </Button>
  );

  const drop =
    photoUrl === null ? null : (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={remove.isPending}
        onClick={() => remove.mutate()}
      >
        {MEMBER_ACCOUNT_COPY.photoRemove}
      </Button>
    );

  return (
    <div className="mb-6 flex flex-wrap items-center gap-[18px]">
      {/* §2.15's disc: 72px, `background:var(--spark-gradient)`, white initials at `font-brand`
          800 24px. It was the `Avatar`'s default fallback — a `brand-100` tint with `brand-700`
          letters, which is the treatment a *list* of faces uses — so the one avatar on this screen
          that is the member themselves was drawn as though it were somebody in a roster. `tone`
          is a prop rather than a class here because the fallback is inside the primitive. */}
      <Avatar
        name={fullName}
        src={photoUrl}
        size="lg"
        tone="spark"
        className="h-[72px] w-[72px] flex-none text-2xl"
      />
      {/* `basis` rather than a bare `flex-1`: with `min-w-0` alone this block shrinks to nothing
            before the button opposite it ever wraps, which at 375px truncated a member's own name
            to "D…" over "Fou…". Caught in the browser at phone width, not by a test. The basis is
            the narrowest this may get before the row prefers to wrap. */}
      <div className="min-w-0 flex-1 basis-[150px]">
        <p className="truncate text-xl">{fullName}</p>
        {roleLine === null ? null : (
          <p className="truncate text-sm text-muted-foreground">{roleLine}</p>
        )}
      </div>
      {/* §2.15 draws one control here. Remove joins it only once there is something to remove —
            an offer to delete nothing is not an offer. The format hint this block used to carry is
            not drawn: the head row has no line for it, `accept` narrows the picker, and the server
            refuses the rest either way. */}
      <div className="flex flex-none flex-wrap gap-2">
        {pick}
        {drop}
      </div>
      {fileInput}
    </div>
  );
}
